import { FC } from 'react';
import { Field } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from '@mui/icons-material';
import { Box, InputAdornment } from '@mui/material';

import FileUploadErrors from '@components/FormFileUploadErrors/FormFileUploadErrors';
import FileUploadField from '@components/FormFileUploadField/FileUploadField';
import FormSelect from '@components/FormSelect/FormSelect';
import FormTextField from '@components/FormTextField/FormTextField';
import { fileUploadErrorsSelector } from '@store/features/fileUploadState/selectors';
import useSupplementCategories from '@store/hooks/useSupplementCategories';
import removeWhiteSpaces from '@utils/removeWhiteSpaces';

import { IUserEnteredSupplementFormProps } from './UserEnteredSupplementForm.interface';

import './style.scss';

const uploadImagesSliderResponsiveSettings = [
  {
    breakpoint: 370,
    settings: {
      slidesToShow: 1,
    },
  },
  {
    breakpoint: 480,
    settings: {
      slidesToShow: 2,
    },
  },
  {
    breakpoint: 1080,
    settings: {
      slidesToShow: 3,
    },
  },
];

const UserEnteredSupplementForm: FC<IUserEnteredSupplementFormProps> = ({
  disabled,
  handleSubmit,
}) => {
  const { t } = useTranslation();

  const supplementCategories = useSupplementCategories();
  const fileUploadErrors = useSelector(fileUploadErrorsSelector);

  return (
    <form
      className="user-entered-supplement-form"
      id="user-entered-supplement-form"
      noValidate
      onSubmit={handleSubmit}
    >
      <p className="user-entered-supplement-form__required small">{t('common.required')}</p>

      <Field
        component={FormTextField}
        className="user-entered-supplement-form__input"
        label={t('addSupplementModal.supplementName')}
        name="supplementName"
        disabled={disabled}
        maxRows={2}
        format={removeWhiteSpaces}
        formatOnBlur
        multiline
        required
      />

      <Field
        component={FormTextField}
        className="user-entered-supplement-form__input"
        label={t('addSupplementModal.companyName')}
        name="companyName"
        disabled={disabled}
        format={removeWhiteSpaces}
        formatOnBlur
        multiline
        required
      />

      <Field
        component={FormSelect}
        className="user-entered-supplement-form__input"
        label={t('addSupplementModal.productCategory')}
        name="supplementCategoryId"
        options={supplementCategories}
        disabled={disabled}
      />

      <Field
        component={FormTextField}
        className="user-entered-supplement-form__input user-entered-supplement-form__link"
        label={t('addSupplementModal.linkToProductPage')}
        name="linkToProductPage"
        disabled={disabled}
        InputProps={{
          startAdornment: (
            <InputAdornment className="user-entered-supplement-form__input-icon" position="start">
              <Link color="inherit" />
            </InputAdornment>
          ),
        }}
        format={removeWhiteSpaces}
        formatOnBlur
        required
      />

      {!disabled && (
        <>
          <Field
            name="images"
            component={FileUploadField}
            responsiveImagesSettings={uploadImagesSliderResponsiveSettings}
            className="user-entered-supplement-form__input"
            label={t('addSupplementModal.linkToProductPage')}
            disabled={disabled}
            InputProps={{
              startAdornment: (
                <InputAdornment
                  className="user-entered-supplement-form__input-icon"
                  position="start"
                >
                  <Link color="inherit" />
                </InputAdornment>
              ),
            }}
            formatOnBlur
          />
          {!!fileUploadErrors.length && (
            <Box className="user-entered-supplement-form__file-errors">
              <FileUploadErrors />
            </Box>
          )}
        </>
      )}
    </form>
  );
};

export default UserEnteredSupplementForm;
